import { useMemo, useState } from 'react';
import { collections, categories } from '@/mocks/content';

interface CollectionsSectionProps {
  activeCategory: string;
  onSelectCategory: (id: string) => void;
}

export default function CollectionsSection({ activeCategory, onSelectCategory }: CollectionsSectionProps) {
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('');

  const allTags = useMemo(() => {
    const set = new Set<string>();
    collections.forEach((c) => c.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, []);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return collections.filter((c) => {
      const matchCategory = !activeCategory || c.categoryId === activeCategory;
      const matchTag = !activeTag || c.tags.includes(activeTag);
      const matchKeyword =
        !keyword ||
        c.title.toLowerCase().includes(keyword) ||
        c.desc.toLowerCase().includes(keyword) ||
        c.author.toLowerCase().includes(keyword) ||
        c.tags.some((t) => t.toLowerCase().includes(keyword));
      return matchCategory && matchTag && matchKeyword;
    });
  }, [activeCategory, activeTag, search]);

  const activeCategoryName = categories.find((c) => c.id === activeCategory)?.name;
  const hasFilter = Boolean(activeCategory || activeTag || search.trim());

  const resetAll = () => {
    onSelectCategory('');
    setActiveTag('');
    setSearch('');
  };

  return (
    <section id="collections" className="scroll-mt-nav py-12 md:py-16 bg-background-100/60">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-stack-line text-primary-500"></i>
                精选合集
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                按分类、标签与关键词自由组合筛选，所有结果都来自本页已整理的合集。
              </p>
            </div>
            <div className="relative w-full sm:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索标题、标签或作者"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-foreground-500">标签：</span>
            <button
              type="button"
              onClick={() => setActiveTag('')}
              className={`px-3 py-1.5 text-xs rounded-full border cursor-pointer transition-colors whitespace-nowrap ${
                !activeTag
                  ? 'bg-secondary-500 border-secondary-500 text-background-50'
                  : 'bg-background-50 border-background-300 text-foreground-700 hover:border-secondary-400'
              }`}
            >
              全部
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(activeTag === tag ? '' : tag)}
                className={`px-3 py-1.5 text-xs rounded-full border cursor-pointer transition-colors whitespace-nowrap ${
                  activeTag === tag
                    ? 'bg-secondary-500 border-secondary-500 text-background-50'
                    : 'bg-background-50 border-background-300 text-foreground-700 hover:border-secondary-400'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap min-h-[28px]">
            {activeCategoryName && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-medium">
                分类：{activeCategoryName}
                <button
                  type="button"
                  aria-label="清除分类筛选"
                  onClick={() => onSelectCategory('')}
                  className="w-4 h-4 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </span>
            )}
            <span className="text-xs text-foreground-600">
              共 <strong className="text-primary-700">{filtered.length}</strong> 个合集
            </span>
            {hasFilter && (
              <button
                type="button"
                onClick={resetAll}
                className="text-xs text-foreground-600 underline underline-offset-2 cursor-pointer hover:text-primary-700"
              >
                清除全部筛选
              </button>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filtered.map((item) => (
                <article
                  key={item.id}
                  className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden transition-colors hover:border-primary-300"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面`}
                      title={`${item.title} - 91精选合集`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-background-50/90 text-[11px] font-medium text-primary-700">
                      {item.categoryName}
                    </span>
                    <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-foreground-950/70 text-[11px] text-background-50">
                      {item.duration}
                    </span>
                  </div>
                  <div className="flex flex-col flex-1 p-4">
                    <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2">{item.desc}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-accent-50 text-accent-800 text-[11px]"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-background-100 flex items-center justify-between text-xs text-foreground-600">
                      <span className="flex items-center gap-1 truncate">
                        <i className="ri-user-3-line"></i>
                        {item.author}
                      </span>
                      <span className="flex items-center gap-3 flex-shrink-0">
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-primary-500"></i>
                          {item.rating}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {item.views}
                        </span>
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center py-16 rounded-lg border border-dashed border-background-300 bg-background-50">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <p className="mt-3 text-sm text-foreground-700">没有找到匹配的合集</p>
              <p className="mt-1 text-xs text-foreground-500">试试更换关键词，或清除当前的分类与标签筛选。</p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-4 px-4 py-2 text-sm rounded-md bg-primary-500 text-background-50 cursor-pointer whitespace-nowrap transition-colors hover:bg-primary-600"
              >
                清除筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}